import React, { useEffect, useRef, useState, useCallback } from "react";
import { Stage, Layer, Group, Path, Line, Rect, Circle, Ellipse, Text, Label, Tag } from "react-konva";
// import Konva from "konva"; // only needed by the disabled zoom feature below (Konva.Tween)
import {
  fieldToCell,
  roundField,
  clamp,
  // cellBounds, // only needed by the disabled auto-zoom/cell-highlight feature below
  FIELD_WIDTH_UNITS,
  FIELD_HEIGHT_UNITS,
} from "../../utils/fieldCoordinateTransform.utils";

// Grid-to-coordinate field map (PRD-Grid-to-Coordinate-System.md), rendered
// with Konva (canvas).
//
// The placement gesture — press, optionally drag to fine-tune, release —
// always resolves directly to a precise (x, y) field coordinate and commits
// it; that part never changes. Manual pinch-to-zoom (touch) and scroll-wheel
// zoom (desktop) can be switched on/off via the on-screen toggle (top-right
// of the map) — off by default. Turning it off snaps the view back to the
// default fitted scale.
//
// A separate two-tap "select cell, auto-zoom in, then confirm" flow was also
// built and is kept further below as a commented-out block (see "AUTO-ZOOM
// FEATURE — disabled") in case it's wanted again later; it is not active.
//
// Perf notes:
// - The 10x10-per-cell sub-grid is never materialized as individual nodes —
//   it's pure math (fieldCoordinateTransform.utils.js).
// - Static content (pitch, grid, numbers, badges) lives on its own Layer,
//   authored directly in field-unit space (0-100 x, 0-80 y).
// - The drag-to-place marker lives on a second, always-unscaled Layer and is
//   moved imperatively (node.position + layer.batchDraw), so a drag never
//   triggers a React re-render.
// - Manual zoom/pan (when toggled on) is also applied imperatively to the
//   content Layer's scale/position, rAF-throttled — no React state during a
//   live pinch/wheel gesture.

const Y = (fieldY) => FIELD_HEIGHT_UNITS - fieldY; // field y grows up; canvas y grows down
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
// const AUTO_ZOOM = 3; // zoom level tap 1 (cell select) animates in to — disabled auto-zoom feature only
// const AUTO_ZOOM_ANIM_MS = 180;
const PITCH_LINE_COLOR = "#2f8f4e";

// Pitch marking geometry, in field units. The penalty box is deliberately
// grid-aligned (20 deep x 40 wide) rather than metric, so the markings below
// are derived from that same convention: 20u/16.5m = 1.212 u/m in x,
// 40u/40.32m = 0.992 u/m in y.
const PEN_BOX_DEPTH = 20;
const PEN_SPOT_X = 14; // 11 m from the goal line — also the D's centre
const D_RADIUS = 9; // 9.15 m, in x-units
const SIX_BOX_DEPTH = 6.7; // 5.5 m
const SIX_BOX_HALF_W = 9.1; // 18.32 m / 2
const CORNER_RADIUS = 1.2; // 1 m
const SPOT_RADIUS = 0.6;

// Half-angle at which the D crosses the penalty-box edge, so the arc meets the
// box exactly instead of overshooting into it.
const D_HALF_ANGLE =
  (Math.acos((PEN_BOX_DEPTH - PEN_SPOT_X) / D_RADIUS) * 180) / Math.PI;

// Elliptical-arc path in canvas space. Angles are degrees from the +x axis
// measured towards field-up, i.e. the natural pitch orientation.
// rx/ry differ because the content Layer is scaled non-uniformly (see `aspect`).
const arcPath = (cx, cy, rx, ry, a0, a1) => {
  const pt = (a) => {
    const r = (a * Math.PI) / 180;
    return { x: cx + rx * Math.cos(r), y: cy - ry * Math.sin(r) };
  };
  const s = pt(a0);
  const e = pt(a1);
  const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  // Increasing angle runs counter-clockwise on screen -> sweep flag 0.
  const sweep = a1 > a0 ? 0 : 1;
  return ["M", s.x, s.y, "A", rx, ry, 0, large, sweep, e.x, e.y].join(" ");
};

// Reference coordinate points shown as floating labels around the pitch,
// per PRD section 5 (corners, center, goal box corners, goal mouths).
const REFERENCE_POINTS = [
  { x: 0, y: 0 }, { x: 50, y: 0 }, { x: 100, y: 0 },
  { x: 0, y: 20 }, { x: 20, y: 20 }, { x: 80, y: 20 }, { x: 100, y: 20 },
  { x: 0, y: 30 }, { x: 100, y: 30 },
  { x: 50, y: 40 },
  { x: 0, y: 50 }, { x: 100, y: 50 },
  { x: 0, y: 60 }, { x: 20, y: 60 }, { x: 80, y: 60 }, { x: 100, y: 60 },
  { x: 0, y: 80 }, { x: 50, y: 80 }, { x: 100, y: 80 },
];

// Pinch-gesture helpers (used when manual zoom is toggled on).
const getTouchPoint = (touch, box) => ({
  x: touch.clientX - box.left,
  y: touch.clientY - box.top,
});
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const centerOf = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

const CoordinateBadge = ({ x, y }) => {
  const isLeftEdge = x === 0;
  const isRightEdge = x === 100;
  const isTopEdge = y === 80;
  const isBottomEdge = y === 0;

  let bx = x;
  let by = Y(y);
  if (isLeftEdge) bx -= 8;
  else if (isRightEdge) bx += 1;
  else bx -= 4;
  if (isTopEdge) by -= 5;
  else if (isBottomEdge) by += 1.5;
  else by -= 6;

  return (
    <Label x={bx} y={by} listening={false}>
      <Tag fill="#ffffff" stroke="#b8b8b8" strokeWidth={0.15} cornerRadius={0.8} />
      <Text
        text={`${x},${y}`}
        fontSize={2.8}
        fontStyle="bold"
        fill="#1d1d1d"
        padding={0.6}
      />
    </Label>
  );
};

// Small on/off switch for FieldMapV2's zoomEnabled prop. Exported so a
// parent (e.g. InputBoard.js, placed below its "DONE" button) can render and
// own the toggle itself rather than FieldMapV2 floating it over the pitch.
export const ZoomToggleButton = ({ zoomEnabled, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={zoomEnabled}
    aria-label="Toggle manual pinch/wheel zoom"
    onClick={onToggle}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      height: 22,
      padding: "0 8px",
      borderRadius: 11,
      border: "none",
      background: "#fff",
      boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
      cursor: "pointer",
      touchAction: "manipulation",
      WebkitTapHighlightColor: "transparent",
    }}
  >
    <span style={{ fontSize: 10, fontWeight: 700, color: "#1d1d1d" }}>Zoom</span>
    <span
      aria-hidden="true"
      style={{
        position: "relative",
        width: 22,
        height: 12,
        borderRadius: 6,
        background: zoomEnabled ? "#2699fb" : "#d6d6d6",
        transition: "background 0.15s ease",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: zoomEnabled ? 12 : 2,
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 2px rgba(0,0,0,0.35)",
          transition: "left 0.15s ease",
        }}
      />
    </span>
  </button>
);

// Static pitch markings + 80-cell grid + 1-unit reference sub-grid, all
// authored directly in field-unit space so the Layer's own scale is the
// only transform needed.
// `aspect` = scaleX / scaleY of the content Layer. The Layer is fitted to the
// container on each axis independently, so a field-unit circle would otherwise
// render as a stretched ellipse. Giving every round marking a y-radius of
// r * aspect makes rx*scaleX === ry*scaleY, i.e. a true circle on screen.
const StaticContent = ({ aspect = 1 }) => {
  const cx = 50;
  const cy = Y(40);
  const ry = (r) => r * aspect;

  const majorLines = [];
  for (let i = 0; i <= 10; i++) {
    majorLines.push(
      <Line key={`mv${i}`} points={[i * 10, 0, i * 10, 80]} stroke="#3a3a3a" strokeWidth={0.25} listening={false} perfectDrawEnabled={false} />
    );
  }
  for (let i = 0; i <= 8; i++) {
    majorLines.push(
      <Line key={`mh${i}`} points={[0, i * 10, 100, i * 10]} stroke="#3a3a3a" strokeWidth={0.25} listening={false} perfectDrawEnabled={false} />
    );
  }

  const fineLines = [];
  for (let i = 0; i <= 100; i++) {
    if (i % 10 === 0) continue;
    fineLines.push(
      <Line
        key={`fv${i}`}
        points={[i, 0, i, 80]}
        stroke={i % 5 === 0 ? "#e0847a" : "#bcd4f0"}
        strokeWidth={0.08}
        listening={false}
        perfectDrawEnabled={false}
      />
    );
  }
  for (let i = 0; i <= 80; i++) {
    if (i % 10 === 0) continue;
    fineLines.push(
      <Line
        key={`fh${i}`}
        points={[0, i, 100, i]}
        stroke={i % 5 === 0 ? "#e0847a" : "#bcd4f0"}
        strokeWidth={0.08}
        listening={false}
        perfectDrawEnabled={false}
      />
    );
  }

  const cellNumbers = [];
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 10; col++) {
      const num = row * 10 + col + 1;
      const fontSize = 3.4;
      cellNumbers.push(
        <Text
          key={`n${num}`}
          x={col * 10}
          y={Y(row * 10 + 5) - fontSize / 2}
          width={10}
          align="center"
          fontSize={fontSize}
          fontStyle="bold"
          fill="#9aa0a6"
          text={String(num)}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
    }
  }

  // Crosshatch fill for the two goal-mouth boxes (manual diagonal lines —
  // Konva has no simple vector pattern-fill, so this stands in for the
  // SVG <pattern> used previously).
  const hatch = (boxX) => {
    const lines = [];
    for (let i = -20; i <= 20; i += 1.4) {
      lines.push(
        <Line
          key={`hatch-${boxX}-${i}`}
          points={[boxX, Y(50) + i, boxX + 4, Y(50) + i + 4]}
          stroke={PITCH_LINE_COLOR}
          strokeWidth={0.15}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
    }
    return lines;
  };

  return (
    <>
      {fineLines}
      {majorLines}

      {/* pitch outline */}
      <Rect x={0} y={0} width={100} height={80} stroke={PITCH_LINE_COLOR} strokeWidth={0.6} listening={false} perfectDrawEnabled={false} />
      {/* halfway line + center circle/spot */}
      <Line points={[50, 0, 50, 80]} stroke={PITCH_LINE_COLOR} strokeWidth={0.5} listening={false} perfectDrawEnabled={false} />
      <Ellipse x={cx} y={cy} radiusX={D_RADIUS} radiusY={ry(D_RADIUS)} stroke={PITCH_LINE_COLOR} strokeWidth={0.5} listening={false} perfectDrawEnabled={false} />
      <Ellipse x={cx} y={cy} radiusX={SPOT_RADIUS} radiusY={ry(SPOT_RADIUS)} fill={PITCH_LINE_COLOR} listening={false} perfectDrawEnabled={false} />

      {/* corner arcs */}
      <Path data={arcPath(0, Y(0), CORNER_RADIUS, ry(CORNER_RADIUS), 0, 90)} stroke={PITCH_LINE_COLOR} strokeWidth={0.4} listening={false} perfectDrawEnabled={false} />
      <Path data={arcPath(100, Y(0), CORNER_RADIUS, ry(CORNER_RADIUS), 90, 180)} stroke={PITCH_LINE_COLOR} strokeWidth={0.4} listening={false} perfectDrawEnabled={false} />
      <Path data={arcPath(100, Y(80), CORNER_RADIUS, ry(CORNER_RADIUS), 180, 270)} stroke={PITCH_LINE_COLOR} strokeWidth={0.4} listening={false} perfectDrawEnabled={false} />
      <Path data={arcPath(0, Y(80), CORNER_RADIUS, ry(CORNER_RADIUS), 270, 360)} stroke={PITCH_LINE_COLOR} strokeWidth={0.4} listening={false} perfectDrawEnabled={false} />

      {/* left penalty box + 6-yard box + spot + D + goal (clipped to the box so hatch lines don't spill) */}
      <Rect x={0} y={Y(60)} width={PEN_BOX_DEPTH} height={40} stroke={PITCH_LINE_COLOR} strokeWidth={0.5} listening={false} perfectDrawEnabled={false} />
      <Rect x={0} y={Y(40 + SIX_BOX_HALF_W)} width={SIX_BOX_DEPTH} height={SIX_BOX_HALF_W * 2} stroke={PITCH_LINE_COLOR} strokeWidth={0.4} listening={false} perfectDrawEnabled={false} />
      <Ellipse x={PEN_SPOT_X} y={Y(40)} radiusX={SPOT_RADIUS} radiusY={ry(SPOT_RADIUS)} fill={PITCH_LINE_COLOR} listening={false} perfectDrawEnabled={false} />
      <Path data={arcPath(PEN_SPOT_X, Y(40), D_RADIUS, ry(D_RADIUS), -D_HALF_ANGLE, D_HALF_ANGLE)} stroke={PITCH_LINE_COLOR} strokeWidth={0.5} listening={false} perfectDrawEnabled={false} />
      <Group clipX={-4} clipY={Y(50)} clipWidth={4} clipHeight={20} listening={false}>
        <Rect x={-4} y={Y(50)} width={4} height={20} stroke={PITCH_LINE_COLOR} strokeWidth={0.4} perfectDrawEnabled={false} />
        {hatch(-4)}
      </Group>

      {/* right penalty box + 6-yard box + spot + D + goal */}
      <Rect x={100 - PEN_BOX_DEPTH} y={Y(60)} width={PEN_BOX_DEPTH} height={40} stroke={PITCH_LINE_COLOR} strokeWidth={0.5} listening={false} perfectDrawEnabled={false} />
      <Rect x={100 - SIX_BOX_DEPTH} y={Y(40 + SIX_BOX_HALF_W)} width={SIX_BOX_DEPTH} height={SIX_BOX_HALF_W * 2} stroke={PITCH_LINE_COLOR} strokeWidth={0.4} listening={false} perfectDrawEnabled={false} />
      <Ellipse x={100 - PEN_SPOT_X} y={Y(40)} radiusX={SPOT_RADIUS} radiusY={ry(SPOT_RADIUS)} fill={PITCH_LINE_COLOR} listening={false} perfectDrawEnabled={false} />
      <Path data={arcPath(100 - PEN_SPOT_X, Y(40), D_RADIUS, ry(D_RADIUS), 180 - D_HALF_ANGLE, 180 + D_HALF_ANGLE)} stroke={PITCH_LINE_COLOR} strokeWidth={0.5} listening={false} perfectDrawEnabled={false} />
      <Group clipX={100} clipY={Y(50)} clipWidth={4} clipHeight={20} listening={false}>
        <Rect x={100} y={Y(50)} width={4} height={20} stroke={PITCH_LINE_COLOR} strokeWidth={0.4} perfectDrawEnabled={false} />
        {hatch(100)}
      </Group>

      {cellNumbers}
      {/* {REFERENCE_POINTS.map((p) => (
        <CoordinateBadge key={`${p.x}-${p.y}`} x={p.x} y={p.y} />
      ))} */}
    </>
  );
};

// zoomEnabled is controlled by the parent (InputBoard.js renders the toggle
// button, e.g. below its "DONE" button) — this component just reacts to it.
const FieldMapV2 = ({ onPress, zoomEnabled = false }) => {
  const wrapperRef = useRef(null);
  const stageRef = useRef(null);
  const contentLayerRef = useRef(null);
  const markerLayerRef = useRef(null);
  const markerRef = useRef(null);

  const [size, setSize] = useState({ width: 0, height: 0 });

  const draggingRef = useRef(false); // single-finger/mouse place gesture active
  const lastFieldPointRef = useRef(null);
  const rafRef = useRef(null);

  // Manual-zoom state (only touched when zoomEnabled).
  const sizeRef = useRef({ width: 0, height: 0 });
  const baseScaleRef = useRef({ x: 1, y: 1 });
  const zoomRef = useRef(MIN_ZOOM);
  const pinchRef = useRef(null); // { lastDist, lastCenter } while a 2-touch pinch is active

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setSize({ width: w, height: w / 1.8 });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const clampPan = useCallback((pos, zoom) => {
    const base = baseScaleRef.current;
    const s = sizeRef.current;
    const contentW = FIELD_WIDTH_UNITS * base.x * zoom;
    const contentH = FIELD_HEIGHT_UNITS * base.y * zoom;
    const minX = Math.min(0, s.width - contentW);
    const minY = Math.min(0, s.height - contentH);
    return { x: clamp(pos.x, minX, 0), y: clamp(pos.y, minY, 0) };
  }, []);

  // Sets the content layer's scale/position directly (no animation) —
  // used for both the base fit-to-container sizing and, when zoom is
  // enabled, live pinch/wheel updates.
  const applyTransform = useCallback((zoom, pos) => {
    const layer = contentLayerRef.current;
    if (!layer) return;
    const clampedZoom = clamp(zoom, MIN_ZOOM, MAX_ZOOM);
    const base = baseScaleRef.current;
    const clampedPos = clampPan(pos, clampedZoom);
    zoomRef.current = clampedZoom;
    layer.scale({ x: base.x * clampedZoom, y: base.y * clampedZoom });
    layer.position(clampedPos);
    layer.batchDraw();
  }, [clampPan]);

  // Re-fit on container resize, preserving the current zoom level.
  useEffect(() => {
    sizeRef.current = size;
    if (!size.width) return;
    baseScaleRef.current = { x: size.width / FIELD_WIDTH_UNITS, y: size.height / FIELD_HEIGHT_UNITS };
    const layer = contentLayerRef.current;
    applyTransform(zoomRef.current, layer ? { x: layer.x(), y: layer.y() } : { x: 0, y: 0 });
  }, [size, applyTransform]);

  // Turning zoom off snaps the view back to the default fitted scale.
  useEffect(() => {
    if (!zoomEnabled) {
      pinchRef.current = null;
      applyTransform(MIN_ZOOM, { x: 0, y: 0 });
    }
  }, [zoomEnabled, applyTransform]);

  const updateMarkerFromPointer = useCallback(() => {
    const stage = stageRef.current;
    const layer = contentLayerRef.current;
    if (!stage || !layer) return;
    const fieldPos = layer.getRelativePointerPosition(); // layer-local, SVG-y-down space
    const pixelPos = stage.getPointerPosition();
    if (!fieldPos || !pixelPos) return;
    lastFieldPointRef.current = { x: fieldPos.x, y: FIELD_HEIGHT_UNITS - fieldPos.y };
    const marker = markerRef.current;
    if (marker) {
      // Sits exactly on the pointer, so the ring marks the point that gets
      // committed from lastFieldPointRef above.
      marker.position({ x: pixelPos.x, y: pixelPos.y });
      marker.opacity(1);
      markerLayerRef.current.batchDraw();
    }
  }, []);

  const hideMarker = useCallback(() => {
    const marker = markerRef.current;
    if (marker) {
      marker.opacity(0);
      markerLayerRef.current.batchDraw();
    }
  }, []);

  const beginPlace = useCallback(() => {
    draggingRef.current = true;
    updateMarkerFromPointer();
  }, [updateMarkerFromPointer]);

  const movePlace = useCallback(() => {
    if (!draggingRef.current || rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      updateMarkerFromPointer();
    });
  }, [updateMarkerFromPointer]);

  const cancelPlace = useCallback(() => {
    draggingRef.current = false;
    lastFieldPointRef.current = null;
    hideMarker();
  }, [hideMarker]);

  const commitPlace = useCallback(() => {
    draggingRef.current = false;
    hideMarker();
    const field = lastFieldPointRef.current;
    lastFieldPointRef.current = null;
    if (!field) return;
    const rounded = roundField(field);
    const cell = fieldToCell(rounded.x, rounded.y);
    onPress(cell, rounded);
  }, [hideMarker, onPress]);

  // --- Mouse (desktop) ---
  const onMouseDown = useCallback((e) => {
    if (e.evt.button !== 0) return;
    beginPlace();
  }, [beginPlace]);
  const onMouseMove = movePlace;
  const onMouseUp = useCallback(() => {
    if (draggingRef.current) commitPlace();
  }, [commitPlace]);

  // Scroll-wheel zoom, centered on the cursor — only when the toggle is on.
  const onWheel = useCallback((e) => {
    if (!zoomEnabled) return;
    e.evt.preventDefault();
    const stage = stageRef.current;
    const layer = contentLayerRef.current;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const base = baseScaleRef.current;
    const oldZoom = zoomRef.current;
    const oldScale = { x: base.x * oldZoom, y: base.y * oldZoom };
    const pointTo = {
      x: (pointer.x - layer.x()) / oldScale.x,
      y: (pointer.y - layer.y()) / oldScale.y,
    };
    const scaleBy = 1.08;
    const newZoom = clamp(
      e.evt.deltaY > 0 ? oldZoom / scaleBy : oldZoom * scaleBy,
      MIN_ZOOM,
      MAX_ZOOM
    );
    const newScale = { x: base.x * newZoom, y: base.y * newZoom };
    const newPos = {
      x: pointer.x - pointTo.x * newScale.x,
      y: pointer.y - pointTo.y * newScale.y,
    };
    applyTransform(newZoom, newPos);
  }, [zoomEnabled, applyTransform]);

  // --- Touch (mobile): 1 finger always places the coordinate. 2 fingers
  // pinch-zoom/pan, but only when the toggle is on. ---
  const onTouchStart = useCallback((e) => {
    const touches = e.evt.touches;
    if (touches.length === 1) {
      // Suppress the browser's synthetic compatibility mouse events
      // (mousedown/mouseup fired ~300ms after a real touch) — otherwise a
      // single tap ends up calling onPress twice: once for the touch, once
      // for the synthesized "click", which InputBoard.js sees as two taps
      // (setting both start and end from what was really one tap).
      e.evt.preventDefault();
      beginPlace();
    } else if (zoomEnabled && touches.length >= 2) {
      cancelPlace();
      const box = stageRef.current.container().getBoundingClientRect();
      const p1 = getTouchPoint(touches[0], box);
      const p2 = getTouchPoint(touches[1], box);
      pinchRef.current = { lastDist: distance(p1, p2), lastCenter: centerOf(p1, p2) };
    }
  }, [beginPlace, cancelPlace, zoomEnabled]);

  const onTouchMove = useCallback((e) => {
    const touches = e.evt.touches;
    if (zoomEnabled && touches.length >= 2 && pinchRef.current) {
      e.evt.preventDefault();
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (!pinchRef.current) return;
        const box = stageRef.current.container().getBoundingClientRect();
        const p1 = getTouchPoint(touches[0], box);
        const p2 = getTouchPoint(touches[1], box);
        const dist = distance(p1, p2);
        const center = centerOf(p1, p2);
        const base = baseScaleRef.current;
        const layer = contentLayerRef.current;
        const oldZoom = zoomRef.current;
        const oldScale = { x: base.x * oldZoom, y: base.y * oldZoom };
        const pointTo = {
          x: (pinchRef.current.lastCenter.x - layer.x()) / oldScale.x,
          y: (pinchRef.current.lastCenter.y - layer.y()) / oldScale.y,
        };
        const scaleDelta = pinchRef.current.lastDist ? dist / pinchRef.current.lastDist : 1;
        const newZoom = clamp(oldZoom * scaleDelta, MIN_ZOOM, MAX_ZOOM);
        const newScale = { x: base.x * newZoom, y: base.y * newZoom };
        const newPos = {
          x: center.x - pointTo.x * newScale.x,
          y: center.y - pointTo.y * newScale.y,
        };
        applyTransform(newZoom, newPos);
        pinchRef.current = { lastDist: dist, lastCenter: center };
      });
    } else if (touches.length === 1 && draggingRef.current) {
      movePlace();
    }
  }, [zoomEnabled, applyTransform, movePlace]);

  const onTouchEnd = useCallback((e) => {
    const touches = e.evt.touches;
    if (touches.length === 0) {
      e.evt.preventDefault();
      if (pinchRef.current) {
        pinchRef.current = null;
      } else if (draggingRef.current) {
        commitPlace();
      }
    } else if (touches.length === 1) {
      // Coming down from a pinch (2 -> 1 finger): end the pinch without
      // committing a placement. A fresh touchstart starts a new gesture.
      pinchRef.current = null;
    }
  }, [commitPlace]);

  /* ============================================================
   * AUTO-ZOOM FEATURE — disabled for now, kept here (commented out) so it
   * can be restored later instead of rebuilt from scratch.
   *
   * Manual pinch/wheel zoom is already active above (gated by the on-screen
   * toggle) — this block is the extra, separate two-tap flow on top of that:
   * tap 1 picks + highlights one of the 80 outer grid cells and animates the
   * view zoomed in on it (persisting while waiting); tap 2, inside that
   * cell, is a direct pixel click that commits the precise coordinate and
   * animates the view back to the default fitted zoom.
   *
   * To re-enable:
   *   1. Uncomment the `Konva` and `cellBounds` imports at the top of this
   *      file, and the AUTO_ZOOM/AUTO_ZOOM_ANIM_MS constants.
   *   2. Uncomment this whole block and wire its refs/functions in above
   *      instead of the simple versions (the ones below shadow/replace
   *      beginPlace, cancelPlace, commitPlace); add the highlightRef <Rect>
   *      back inside the content <Layer>.
   *
   * const highlightRef = useRef(null);
   * // Two-tap flow: tap 1 picks the outer (80-cell) grid cell and highlights it;
   * // tap 2, inside that cell, places the precise pixel-click coordinate.
   * const phaseRef = useRef("idle"); // "idle" | "cellSelected"
   * const pendingCommitRef = useRef(false); // true while the current press is tap 2 (the confirm tap)
   * const tweenRef = useRef(null);
   *
   * const stopTween = useCallback(() => {
   *   if (tweenRef.current) {
   *     tweenRef.current.destroy();
   *     tweenRef.current = null;
   *   }
   * }, []);
   *
   * // NOTE: applyTransform above would need a stopTween() call added at its
   * // start so a live pinch/wheel always wins over an in-flight auto-zoom tween.
   *
   * const animateTransform = useCallback((zoom, pos, duration = AUTO_ZOOM_ANIM_MS) => {
   *   const layer = contentLayerRef.current;
   *   if (!layer) return;
   *   stopTween();
   *   const clampedZoom = clamp(zoom, MIN_ZOOM, MAX_ZOOM);
   *   const base = baseScaleRef.current;
   *   const clampedPos = clampPan(pos, clampedZoom);
   *   zoomRef.current = clampedZoom;
   *   tweenRef.current = new Konva.Tween({
   *     node: layer,
   *     duration: duration / 1000,
   *     easing: Konva.Easings.EaseInOut,
   *     scaleX: base.x * clampedZoom,
   *     scaleY: base.y * clampedZoom,
   *     x: clampedPos.x,
   *     y: clampedPos.y,
   *     onFinish: () => {
   *       tweenRef.current = null;
   *     },
   *   });
   *   tweenRef.current.play();
   * }, [clampPan, stopTween]);
   *
   * const resetView = useCallback(() => {
   *   animateTransform(MIN_ZOOM, { x: 0, y: 0 });
   * }, [animateTransform]);
   *
   * // Re-fit on container resize, preserving the current zoom level.
   * useEffect(() => {
   *   sizeRef.current = size;
   *   if (!size.width) return;
   *   baseScaleRef.current = { x: size.width / FIELD_WIDTH_UNITS, y: size.height / FIELD_HEIGHT_UNITS };
   *   const layer = contentLayerRef.current;
   *   applyTransform(zoomRef.current, layer ? { x: layer.x(), y: layer.y() } : { x: 0, y: 0 });
   * }, [size, applyTransform]);
   *
   * const showCellHighlight = useCallback((cell) => {
   *   const rect = highlightRef.current;
   *   if (!rect) return;
   *   const b = cellBounds(cell);
   *   rect.position({ x: b.x, y: FIELD_HEIGHT_UNITS - b.y - b.height });
   *   rect.size({ width: b.width, height: b.height });
   *   rect.opacity(1);
   *   contentLayerRef.current.batchDraw();
   * }, []);
   *
   * const hideCellHighlight = useCallback(() => {
   *   const rect = highlightRef.current;
   *   if (rect) {
   *     rect.opacity(0);
   *     contentLayerRef.current.batchDraw();
   *   }
   * }, []);
   *
   * // Two-tap flow:
   * //   tap 1 (phase "idle")        -> picks + highlights the outer grid cell, waits.
   * //   tap 2 (phase "cellSelected") -> a direct pixel click inside that cell; places
   * //                                   the precise coordinate on release.
   * const beginPlaceZoom = useCallback(() => {
   *   draggingRef.current = true;
   *   pendingCommitRef.current = phaseRef.current === "cellSelected";
   *   updateMarkerFromPointer(); // note: would also need the phase-aware highlight preview branch
   * }, [updateMarkerFromPointer]);
   *
   * const cancelPlaceZoom = useCallback(() => {
   *   draggingRef.current = false;
   *   pendingCommitRef.current = false;
   *   lastFieldPointRef.current = null;
   *   hideMarker();
   *   if (phaseRef.current === "idle") {
   *     hideCellHighlight();
   *   }
   * }, [hideMarker, hideCellHighlight]);
   *
   * const commitPlaceZoom = useCallback(() => {
   *   draggingRef.current = false;
   *   if (!pendingCommitRef.current) {
   *     // Tap 1 released: the outer cell is now selected — zoom in on it and
   *     // wait (the zoomed view persists) for tap 2 to confirm the precise spot.
   *     phaseRef.current = "cellSelected";
   *     hideMarker();
   *     const field = lastFieldPointRef.current; // field-y-up, where tap 1 landed
   *     if (field) {
   *       const s = sizeRef.current;
   *       const base = baseScaleRef.current;
   *       const targetScale = { x: base.x * AUTO_ZOOM, y: base.y * AUTO_ZOOM };
   *       const svgY = FIELD_HEIGHT_UNITS - field.y;
   *       const targetPos = {
   *         x: s.width / 2 - field.x * targetScale.x,
   *         y: s.height / 2 - svgY * targetScale.y,
   *       };
   *       animateTransform(AUTO_ZOOM, targetPos);
   *     }
   *     return;
   *   }
   *   pendingCommitRef.current = false;
   *   phaseRef.current = "idle";
   *   hideMarker();
   *   hideCellHighlight();
   *   resetView();
   *   const field = lastFieldPointRef.current;
   *   lastFieldPointRef.current = null;
   *   if (!field) return;
   *   const rounded = roundField(field);
   *   const cell = fieldToCell(rounded.x, rounded.y);
   *   onPress(cell, rounded);
   * }, [hideMarker, hideCellHighlight, animateTransform, resetView, onPress]);
   *
   * const onWheel = useCallback((e) => {
   *   e.evt.preventDefault();
   *   const stage = stageRef.current;
   *   const layer = contentLayerRef.current;
   *   const pointer = stage.getPointerPosition();
   *   if (!pointer) return;
   *   const base = baseScaleRef.current;
   *   const oldZoom = zoomRef.current;
   *   const oldScale = { x: base.x * oldZoom, y: base.y * oldZoom };
   *   const pointTo = {
   *     x: (pointer.x - layer.x()) / oldScale.x,
   *     y: (pointer.y - layer.y()) / oldScale.y,
   *   };
   *   const scaleBy = 1.08;
   *   const newZoom = clamp(
   *     e.evt.deltaY > 0 ? oldZoom / scaleBy : oldZoom * scaleBy,
   *     MIN_ZOOM,
   *     MAX_ZOOM
   *   );
   *   const newScale = { x: base.x * newZoom, y: base.y * newZoom };
   *   const newPos = {
   *     x: pointer.x - pointTo.x * newScale.x,
   *     y: pointer.y - pointTo.y * newScale.y,
   *   };
   *   applyTransform(newZoom, newPos);
   * }, [applyTransform]);
   *
   * // 1 finger = place, 2 fingers = pinch-zoom/pan
   * const onTouchStartZoom = useCallback((e) => {
   *   const touches = e.evt.touches;
   *   if (touches.length === 1) {
   *     beginPlaceZoom();
   *   } else if (touches.length >= 2) {
   *     cancelPlaceZoom();
   *     const box = stageRef.current.container().getBoundingClientRect();
   *     const p1 = getTouchPoint(touches[0], box);
   *     const p2 = getTouchPoint(touches[1], box);
   *     pinchRef.current = { lastDist: distance(p1, p2), lastCenter: centerOf(p1, p2) };
   *   }
   * }, [beginPlaceZoom, cancelPlaceZoom]);
   *
   * const onTouchMoveZoom = useCallback((e) => {
   *   const touches = e.evt.touches;
   *   if (touches.length >= 2 && pinchRef.current) {
   *     e.evt.preventDefault();
   *     if (rafRef.current) return;
   *     rafRef.current = requestAnimationFrame(() => {
   *       rafRef.current = null;
   *       if (!pinchRef.current) return;
   *       const box = stageRef.current.container().getBoundingClientRect();
   *       const p1 = getTouchPoint(touches[0], box);
   *       const p2 = getTouchPoint(touches[1], box);
   *       const dist = distance(p1, p2);
   *       const center = centerOf(p1, p2);
   *       const base = baseScaleRef.current;
   *       const layer = contentLayerRef.current;
   *       const oldZoom = zoomRef.current;
   *       const oldScale = { x: base.x * oldZoom, y: base.y * oldZoom };
   *       const pointTo = {
   *         x: (pinchRef.current.lastCenter.x - layer.x()) / oldScale.x,
   *         y: (pinchRef.current.lastCenter.y - layer.y()) / oldScale.y,
   *       };
   *       const scaleDelta = pinchRef.current.lastDist ? dist / pinchRef.current.lastDist : 1;
   *       const newZoom = clamp(oldZoom * scaleDelta, MIN_ZOOM, MAX_ZOOM);
   *       const newScale = { x: base.x * newZoom, y: base.y * newZoom };
   *       const newPos = {
   *         x: center.x - pointTo.x * newScale.x,
   *         y: center.y - pointTo.y * newScale.y,
   *       };
   *       applyTransform(newZoom, newPos);
   *       pinchRef.current = { lastDist: dist, lastCenter: center };
   *     });
   *   } else if (touches.length === 1 && draggingRef.current) {
   *     movePlace();
   *   }
   * }, [applyTransform, movePlace]);
   *
   * const onTouchEndZoom = useCallback((e) => {
   *   const touches = e.evt.touches;
   *   if (touches.length === 0) {
   *     if (pinchRef.current) {
   *       pinchRef.current = null;
   *     } else if (draggingRef.current) {
   *       commitPlaceZoom();
   *     }
   *   } else if (touches.length === 1) {
   *     pinchRef.current = null;
   *   }
   * }, [commitPlaceZoom]);
   *
   * // <Rect ref={highlightRef} stroke="#2699fb" strokeWidth={0.4}
   * //   fill="rgba(38,153,251,0.18)" opacity={0} listening={false}
   * //   perfectDrawEnabled={false} /> — add back inside the content <Layer>.
   * ============================================================ */

  return (
    <div ref={wrapperRef} style={{ width: "100%", display: "flex", justifyContent: "center" }}>
      {!size.width ? (
        <div style={{ padding: 24, color: "#9aa0a6", fontSize: 12 }}>Loading Field Map ...</div>
      ) : (
        <div style={{ width: size.width, height: size.height, margin: "24px 32px", touchAction: "none" }}>
          <Stage
            ref={stageRef}
            width={size.width}
            height={size.height}
            style={{ cursor: "crosshair" }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={cancelPlace}
            onWheel={onWheel}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <Layer ref={contentLayerRef}>
              <StaticContent
                aspect={
                  size.height
                    ? (size.width / FIELD_WIDTH_UNITS) /
                      (size.height / FIELD_HEIGHT_UNITS)
                    : 1
                }
              />
            </Layer>
            <Layer ref={markerLayerRef}>
              <Circle
                ref={markerRef}
                radius={8}
                stroke="#ff3b30"
                strokeWidth={2}
                fill="rgba(255,59,48,0.15)"
                opacity={0}
                listening={false}
              />
            </Layer>
          </Stage>
        </div>
      )}
    </div>
  );
};

export default FieldMapV2;
